/**
 * The client half of the Apple cross-team repair flow.
 *
 * Apple scopes a person's subject identifier — and their Hide-My-Email relay
 * address — to the Apple developer team, and our apps ship under two. A user
 * whose account lives in the other team's namespace signs in here with a
 * subject Firebase has never seen. With a real email address Firebase refuses
 * the second `apple.com` identity and the collision path in `AuthProvider`
 * resolves it. With Hide My Email there is no shared address, so there is
 * nothing to collide on: Firebase creates a brand-new empty account and the
 * sign-in looks like a success.
 *
 * This module is the three steps that fix that:
 *
 *  1. {@link requestRepairPlan} asks the server what to do — the client cannot
 *     work it out, because both halves of the answer (is there an alias row,
 *     and did this sign-in create this account) live server-side;
 *  2. {@link startRepairProbe} hands the browser to the server's Apple flow,
 *     which asks Apple about the other namespace;
 *  3. {@link readRepairFragment} reads the outcome on the way back.
 *
 * An earlier design did step 2 in a popup, using Apple's own JS SDK from this
 * page. That is replaced: the probe runs after the sign-in promise resolves, so
 * no transient user activation is left and browsers block the popup. A
 * navigation needs no activation.
 */

/** Where the server answers what to do about the account just signed in to. */
const PLAN_PATH = '/api/v1/apple/repair-plan'

/** Where the server begins the Apple round trip. */
const START_PATH = '/api/v1/apple/repair-start'

/**
 * Caps the plan request. The user is already signed in and looking at the app,
 * so a hung resolver must not hold the session in limbo — it fails closed to
 * `none`, which leaves them exactly where a user with nothing to repair lands.
 */
const FETCH_TIMEOUT_MS = 5000

/**
 * How long {@link startRepairProbeAfterNotice} shows the "checking for an
 * existing account" notice before handing the browser to the server's Apple
 * flow.
 *
 * The probe is a synchronous top-level navigation -- render the notice and
 * navigate away in the same frame and nobody ever sees it, so a second
 * "Sign in with Apple" prompt appears right after the first with no
 * explanation for why. A fixed, deliberate pause is the whole fix, and 1.5s
 * is comfortably inside the server's two-minute state-cookie freshness
 * window, so it costs nothing on the probe's success path.
 */
export const APPLE_REPAIR_NOTICE_DELAY_MS = 1500

/** Fragment keys the server sends back. See the server's applerepair.go. */
const FRAGMENT_TOKEN = 'apple_token'

/**
 * Query parameter and storage key that bind an outcome to the tab that asked
 * for it.
 *
 * `#apple_token=…` is a bearer credential in a URL, and a Firebase custom
 * token stays valid for an hour — so without this, someone who ran the flow
 * themselves could send a victim `…#apple_token=<their own token>` and the
 * victim's browser would silently sign them into the ATTACKER's account.
 * Everything the victim then did — a hunt, a purchase, an upload — would land
 * there. That is session fixation, and the token being legitimately minted is
 * exactly what makes it work.
 *
 * So the probe mints a nonce, keeps it in `sessionStorage`, and sends a copy
 * out on the return URL's query. A token coming back without the matching
 * nonce was not asked for by this tab and is refused.
 *
 * `sessionStorage` rather than `localStorage` on purpose: it is scoped to this
 * tab, which is precisely the claim being made. It survives navigating away
 * and back, and is not touched by third-party cookie policy.
 */
const BINDING_PARAM = 'apple_repair_binding'
const BINDING_STORAGE_KEY = 'appleRepairBinding'

function mintBinding(): string | undefined {
  try {
    const bytes = new Uint8Array(16)
    crypto.getRandomValues(bytes)
    const binding = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
    window.sessionStorage.setItem(BINDING_STORAGE_KEY, binding)
    return binding
  } catch {
    // No storage (private mode, storage disabled) or no crypto. Without a
    // binding the outcome could not be verified on return, so the probe must
    // not start at all -- failing closed here costs the user nothing they had.
    return undefined
  }
}

/** Reads and consumes the stored binding, so an outcome can be used once. */
function takeBinding(): string | undefined {
  try {
    const stored = window.sessionStorage.getItem(BINDING_STORAGE_KEY)
    window.sessionStorage.removeItem(BINDING_STORAGE_KEY)
    return stored ?? undefined
  } catch {
    return undefined
  }
}
const FRAGMENT_RESULT = 'apple_result'
const FRAGMENT_ERROR = 'apple_error'

export type RepairPlan =
  /** An alias row already names the canonical account. Swap to it. */
  | { kind: 'sign-in'; customToken: string }
  /** No alias, and this account was created by the sign-in in progress. */
  | { kind: 'probe' }
  /** Nothing to do — including every failure, which fails closed to this. */
  | { kind: 'none' }

export type RepairFragment =
  | { kind: 'token'; customToken: string }
  | { kind: 'result'; code: string }
  | { kind: 'error'; code: string }

/**
 * Whether the repair flow is enabled for this deployment.
 *
 * Its own boolean switch rather than riding `identityResolverBaseUrl`: that URL
 * is ALREADY provisioned in production for the (failure-only) collision path,
 * so gating on it alone would put a live cross-origin POST — with the power to
 * swap the session — on every successful Apple sign-in the moment this merges,
 * with no way to turn it off short of a revert.
 *
 * Mirrors `isFeatureEnabled` in apps/wehunt-web/src/appConfig.ts: config.js is
 * produced by envsubst, so an unset variable renders as `""` and a disabled one
 * as the literal string `"false"` — both truthy in plain JS. Only an explicit
 * `true` (a real boolean, or the string `"true"` case-insensitively) turns this
 * on; anything else, including absence, is off.
 */
export function appleResolveOnSuccessEnabled(): boolean {
  const raw: unknown = (window as { __APP_CONFIG__?: Record<string, unknown> }).__APP_CONFIG__?.[
    'appleResolveOnSuccess'
  ]
  if (typeof raw === 'boolean') return raw
  if (typeof raw === 'string') return raw.trim().toLowerCase() === 'true'
  return false
}

/**
 * Asks the server what to do about the account this Apple sign-in landed on.
 *
 * Never throws. Every failure — a network error, a timeout, a non-200, an
 * unparseable or unrecognised body — maps to `none`, because the caller is a
 * sign-in that has ALREADY succeeded and nothing here may turn that into a
 * broken session. An unrecognised `action` is deliberately `none` rather than
 * an error: doing nothing is always safe, so a server that grows a fourth
 * answer cannot break an older client.
 */
export async function requestRepairPlan(idToken: string, baseUrl: string): Promise<RepairPlan> {
  let response: Response
  try {
    response = await fetch(`${baseUrl}${PLAN_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ appleIdToken: idToken }),
      // A redirect here would mean the resolver is not where we think it is;
      // following one could post the token somewhere else entirely.
      redirect: 'error',
      signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
    })
  } catch {
    return { kind: 'none' }
  }
  if (!response.ok) return { kind: 'none' }

  let body: unknown
  try {
    body = await response.json()
  } catch {
    return { kind: 'none' }
  }
  if (typeof body !== 'object' || body === null) return { kind: 'none' }

  const { action, customToken } = body as { action?: unknown; customToken?: unknown }
  if (action === 'signIn') {
    // A signIn answer without a usable token is a server bug, not an
    // instruction — fail closed rather than call Firebase with undefined.
    if (typeof customToken !== 'string' || customToken === '') return { kind: 'none' }
    return { kind: 'sign-in', customToken }
  }
  if (action === 'probe') return { kind: 'probe' }
  return { kind: 'none' }
}

/**
 * Hands the browser to the server's Apple flow.
 *
 * A form POST submitted as a top-level navigation, not a `fetch`, for two
 * independent reasons:
 *
 *  - an ID token in a URL is an ID token in an access log, a proxy log and a
 *    `Referer` header, so it travels in a body;
 *  - the server's state cookie has to be set in a FIRST-party context on the
 *    sso origin. Set from a cross-origin fetch it is a third-party cookie,
 *    which Safari's ITP and Chrome's phase-out may drop silently — and the
 *    flow would then die at the callback with "no state cookie", pointing
 *    nowhere near the cause. A submitted form is a navigation, so the cookie
 *    is first-party there exactly as it is when Apple posts back.
 *
 * The page is left behind, so nothing after this call runs.
 */
export function startRepairProbe(idToken: string, baseUrl: string): boolean {
  const binding = mintBinding()
  if (!binding) return false

  const form = document.createElement('form')
  form.method = 'POST'
  form.action = `${baseUrl}${START_PATH}`
  form.hidden = true

  appendField(form, 'id_token', idToken)
  // Where the server sends the browser back. Deliberately built from the
  // current location rather than read from config: it returns the user exactly
  // where they were, and there is no second place for a return URL to drift
  // out of step with. The fragment is dropped because the server refuses a
  // return URL carrying one — the outcome is appended as a fragment, so one
  // already there would be silently replaced.
  appendField(form, 'return_url', returnUrlWithBinding(binding))

  document.body.appendChild(form)
  form.submit()
  return true
}

/**
 * {@link startRepairProbe}, preceded by a fixed, visible pause.
 *
 * The plan arrives while the user is already `signed-in` (to the shell
 * account), so this is the ONLY point in the flow where a notice can be
 * shown before the browser leaves for Apple -- the caller flips `onPending`
 * to drive an app-level overlay, since the page underneath is about to
 * navigate away regardless of what route it is.
 *
 * `deps` is a test seam, not a configuration point: production always uses
 * the real `startRepairProbe` and a real timer.
 */
export async function startRepairProbeAfterNotice(
  idToken: string,
  baseUrl: string,
  onPending: (pending: boolean) => void,
  deps: { start: typeof startRepairProbe; wait: (ms: number) => Promise<void> } = {
    start: startRepairProbe,
    wait: (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)),
  },
): Promise<boolean> {
  onPending(true)
  try {
    await deps.wait(APPLE_REPAIR_NOTICE_DELAY_MS)
    const started = deps.start(idToken, baseUrl)
    if (!started) {
      // Couldn't mint a binding (no storage, no crypto) -- the probe never
      // left this page, so the notice must not either.
      onPending(false)
    }
    // Otherwise the navigation is imminent and this page is about to be
    // replaced; leaving `pending` true is correct, not a leak.
    return started
  } catch (err) {
    onPending(false)
    throw err
  }
}

function appendField(form: HTMLFormElement, name: string, value: string): void {
  const input = document.createElement('input')
  input.type = 'hidden'
  input.name = name
  input.value = value
  form.appendChild(input)
}

function currentLocationWithoutFragment(): string {
  const { origin, pathname, search } = window.location
  return `${origin}${pathname}${search}`
}

/**
 * The return URL with the binding added to its query.
 *
 * The query rather than the fragment: the server refuses a return URL carrying
 * a fragment (it appends the outcome as one), and it hands the query back
 * untouched. The page's own parameters are preserved — this only adds one.
 */
function returnUrlWithBinding(binding: string): string {
  const url = new URL(currentLocationWithoutFragment())
  url.searchParams.set(BINDING_PARAM, binding)
  return url.toString()
}

/**
 * True once the fragment has been read, so a re-render cannot act on an
 * outcome twice.
 *
 * The fragment is stripped from the URL as it is read, which already stops a
 * reload from replaying it; this latch covers the same page's own re-renders,
 * which happen before any strip is observable. A custom token is single-use,
 * so a second redemption attempt fails and would report a spurious error for
 * a repair that in fact succeeded.
 */
let fragmentRead = false

/**
 * Reads the repair outcome the server put in the fragment, and removes it.
 *
 * Returns `undefined` when there is nothing to act on — no fragment, none of
 * our keys, or it has already been read once.
 *
 * Only the fragment is removed; the query string stays. The page reads its own
 * parameters from the query, and an error outcome still has to render.
 */
export function readRepairFragment(): RepairFragment | undefined {
  if (fragmentRead) return undefined

  const raw = window.location.hash.replace(/^#/, '')
  if (!raw) return undefined

  const params = new URLSearchParams(raw)
  const token = params.get(FRAGMENT_TOKEN)
  const result = params.get(FRAGMENT_RESULT)
  const error = params.get(FRAGMENT_ERROR)
  if (!token && !result && !error) return undefined

  fragmentRead = true

  // Consumed before anything else, and whatever the outcome, so a binding can
  // never be spent twice.
  const expected = takeBinding()
  const presented = new URLSearchParams(window.location.search).get(BINDING_PARAM)
  stripFragmentAndBinding()

  if (token) {
    // Only the credential is bound. A result or error code is a diagnostic,
    // not something anyone gains by replaying, and refusing those on a missing
    // binding would swallow a genuine error report.
    if (!expected || !presented || presented !== expected) {
      return { kind: 'error', code: 'binding_mismatch' }
    }
    return { kind: 'token', customToken: token }
  }
  if (result) return { kind: 'result', code: result }
  return { kind: 'error', code: error as string }
}

function stripFragmentAndBinding(): void {
  try {
    const { pathname, search } = window.location
    const params = new URLSearchParams(search)
    // The binding is a spent nonce, useless on its own, but leaving it in the
    // address bar invites it into a shared link alongside a future outcome.
    params.delete(BINDING_PARAM)
    const query = params.toString()
    window.history.replaceState(window.history.state, '', `${pathname}${query ? `?${query}` : ''}`)
  } catch {
    // replaceState can throw in exotic contexts (a sandboxed frame, an
    // exhausted history quota). The latch above has already made this
    // fragment single-use, so failing to tidy the URL is cosmetic.
  }
}

/** Test seam: clears the latch so each test starts from a clean page. */
export function resetRepairFragmentLatchForTests(): void {
  fragmentRead = false
}
